import { apiClient } from './api';
import type { MenuCategory, MenuItem } from '@/types';

/**
 * MenuService - Menu management API calls
 *
 * Per Restaurant Management Panel: Menu CRUD operations
 * Follows pattern from catalogService.ts
 */

export interface Category {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  displayOrder: number;
  isActive: boolean;
  restaurantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  isAvailable: boolean;
  isActive: boolean;
  displayOrder: number;
  categoryId: string;
  tags?: string;
  calories?: number;
  preparationMinutes?: number;
  stockQuantity?: number | null;
  lowStockThreshold?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductIngredient {
  id: string;
  productId: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const menuService = {
  // ============================================================
  // CATEGORY OPERATIONS
  // ============================================================

  /**
   * Create category
   */
  async createCategory(dto: {
    name: string;
    description?: string;
    imageUrl?: string;
    displayOrder?: number;
  }): Promise<Category> {
    const response = await apiClient.post<Category>('/restaurant/my/categories', dto);
    return response.data.data;
  },

  /**
   * Get all categories
   */
  async getCategories(): Promise<Category[]> {
    const response = await apiClient.get<Category[]>('/restaurant/my/categories');
    return response.data.data;
  },

  /**
   * Update category
   */
  async updateCategory(
    categoryId: string,
    dto: Partial<Category>,
  ): Promise<Category> {
    const response = await apiClient.patch<Category>(
      `/restaurant/my/categories/${categoryId}`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Delete category (soft delete)
   */
  async deleteCategory(categoryId: string): Promise<void> {
    await apiClient.delete(`/restaurant/my/categories/${categoryId}`);
  },

  /**
   * Reorder category
   */
  async reorderCategory(categoryId: string, newOrder: number): Promise<Category> {
    const response = await apiClient.patch<Category>(
      `/restaurant/my/categories/${categoryId}/reorder`,
      { entityId: categoryId, newOrder },
    );
    return response.data.data;
  },

  // ============================================================
  // PRODUCT OPERATIONS
  // ============================================================

  /**
   * Create product
   */
  async createProduct(
    categoryId: string,
    dto: {
      name: string;
      description?: string;
      price: number;
      imageUrl?: string;
      displayOrder?: number;
      tags?: string;
      calories?: number;
      preparationMinutes?: number;
    },
  ): Promise<Product> {
    const response = await apiClient.post<Product>(
      `/restaurant/my/categories/${categoryId}/products`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Get all products
   */
  async getProducts(): Promise<Product[]> {
    const response = await apiClient.get<Product[]>('/restaurant/my/products');
    return response.data.data;
  },

  /**
   * Get single product with modifier groups
   */
  async getProduct(productId: string): Promise<Product> {
    const response = await apiClient.get<Product>(`/restaurant/my/products/${productId}`);
    return response.data.data;
  },

  /**
   * Update product
   */
  async updateProduct(productId: string, dto: Partial<Product>): Promise<Product> {
    const response = await apiClient.patch<Product>(
      `/restaurant/my/products/${productId}`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Delete product (soft delete)
   */
  async deleteProduct(productId: string): Promise<void> {
    await apiClient.delete(`/restaurant/my/products/${productId}`);
  },

  /**
   * Toggle product availability
   */
  async toggleProductAvailability(
    productId: string,
    isAvailable: boolean,
  ): Promise<Product> {
    const response = await apiClient.patch<Product>(
      `/restaurant/my/products/${productId}/availability`,
      { isAvailable },
    );
    return response.data.data;
  },

  /**
   * Reorder product
   */
  async reorderProduct(productId: string, newOrder: number): Promise<Product> {
    const response = await apiClient.patch<Product>(
      `/restaurant/my/products/${productId}/reorder`,
      { entityId: productId, newOrder },
    );
    return response.data.data;
  },

  /**
   * Upload product image
   */
  async uploadProductImage(
    productId: string,
    file: File,
  ): Promise<{ url: string; key: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{ url: string; key: string }>(
      `/restaurant/my/products/${productId}/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data.data;
  },

  /**
   * Upload category image
   * Note: Uses generic upload endpoint since category-specific endpoint doesn't exist yet
   */
  async uploadCategoryImage(
    categoryId: string,
    file: File,
  ): Promise<{ url: string; key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', 'category');
    formData.append('entityId', categoryId);

    const response = await apiClient.post<{ url: string; key: string }>(
      '/upload/image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data.data;
  },

  // ============================================================
  // STOCK OPERATIONS
  // ============================================================

  /**
   * Update product stock
   */
  async updateProductStock(
    productId: string,
    stockQuantity: number | null,
    lowStockThreshold?: number | null,
  ): Promise<Product> {
    const response = await apiClient.patch<Product>(
      `/restaurant/my/products/${productId}/stock`,
      { stockQuantity, lowStockThreshold },
    );
    return response.data.data;
  },

  /**
   * Get low stock products
   */
  async getLowStockProducts(): Promise<Product[]> {
    const response = await apiClient.get<Product[]>('/restaurant/my/products/low-stock');
    return response.data.data;
  },

  // ============================================================
  // OPTION GROUP OPERATIONS
  // ============================================================

  /**
   * Create option group
   */
  async createOptionGroup(productId: string, dto: {
    name: string;
    description?: string;
    minSelect: number;
    maxSelect?: number | null;
    isRequired: boolean;
    displayOrder?: number;
  }): Promise<any> {
    const response = await apiClient.post<any>(
      `/restaurant/my/products/${productId}/option-groups`,
      { ...dto, productId },
    );
    return response.data.data;
  },

  /**
   * Get option groups for a product
   */
  async getOptionGroups(productId: string): Promise<any[]> {
    const response = await apiClient.get<any[]>(`/restaurant/my/products/${productId}/option-groups`);
    return response.data.data;
  },

  /**
   * Update option group
   */
  async updateOptionGroup(optionGroupId: string, dto: Partial<any>): Promise<any> {
    const response = await apiClient.patch<any>(
      `/restaurant/my/option-groups/${optionGroupId}`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Delete option group
   */
  async deleteOptionGroup(optionGroupId: string): Promise<void> {
    await apiClient.delete(`/restaurant/my/option-groups/${optionGroupId}`);
  },

  // ============================================================
  // OPTION OPERATIONS
  // ============================================================

  /**
   * Create option
   */
  async createOption(optionGroupId: string, dto: {
    name: string;
    description?: string;
    priceModifier: number;
    displayOrder?: number;
    isDefault?: boolean;
  }): Promise<any> {
    const response = await apiClient.post<any>(
      `/restaurant/my/option-groups/${optionGroupId}/options`,
      { ...dto, optionGroupId },
    );
    return response.data.data;
  },

  /**
   * Get options for an option group
   */
  async getOptions(optionGroupId: string): Promise<any[]> {
    const response = await apiClient.get<any[]>(`/restaurant/my/option-groups/${optionGroupId}/options`);
    return response.data.data;
  },

  /**
   * Update option
   */
  async updateOption(optionId: string, dto: Partial<any>): Promise<any> {
    const response = await apiClient.patch<any>(
      `/restaurant/my/options/${optionId}`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Delete option
   */
  async deleteOption(optionId: string): Promise<void> {
    await apiClient.delete(`/restaurant/my/options/${optionId}`);
  },

  // ============================================================
  // MODIFIER GROUP OPERATIONS
  // ============================================================

  /**
   * Get modifier groups for restaurant
   */
  async getModifierGroups(): Promise<any[]> {
    const response = await apiClient.get<any[]>('/restaurant/my/modifier-groups');
    return response.data.data;
  },

  /**
   * Update modifier group
   */
  async updateModifierGroup(modifierGroupId: string, dto: Partial<any>): Promise<any> {
    const response = await apiClient.patch<any>(
      `/restaurant/my/modifier-groups/${modifierGroupId}`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Delete modifier group
   */
  async deleteModifierGroup(modifierGroupId: string): Promise<void> {
    await apiClient.delete(`/restaurant/my/modifier-groups/${modifierGroupId}`);
  },

  // ============================================================
  // MODIFIER OPTION OPERATIONS
  // ============================================================

  /**
   * Create modifier option
   */
  async createModifierOption(modifierGroupId: string, dto: {
    name: string;
    description?: string;
    priceAdjustment?: number;
    calories?: number | null;
    displayOrder?: number;
    isDefault?: boolean;
  }): Promise<any> {
    const response = await apiClient.post<any>(
      `/restaurant/my/modifier-groups/${modifierGroupId}/modifier-options`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Get modifier options for a modifier group
   */
  async getModifierOptions(modifierGroupId: string): Promise<any[]> {
    const response = await apiClient.get<any[]>(
      `/restaurant/my/modifier-groups/${modifierGroupId}/modifier-options`,
    );
    return response.data.data;
  },

  /**
   * Update modifier option
   */
  async updateModifierOption(modifierOptionId: string, dto: Partial<any>): Promise<any> {
    const response = await apiClient.patch<any>(
      `/restaurant/my/modifier-options/${modifierOptionId}`,
      dto,
    );
    return response.data.data;
  },

  /**
   * Delete modifier option
   */
  async deleteModifierOption(modifierOptionId: string): Promise<void> {
    await apiClient.delete(`/restaurant/my/modifier-options/${modifierOptionId}`);
  },

  // ============================================================
  // PRODUCT INGREDIENTS (removable)
  // ============================================================

  async getProductIngredients(productId: string): Promise<ProductIngredient[]> {
    const response = await apiClient.get<ProductIngredient[]>(
      `/restaurant/my/products/${productId}/ingredients`,
    );
    return response.data.data;
  },

  async createProductIngredient(
    productId: string,
    dto: { name: string; displayOrder?: number },
  ): Promise<ProductIngredient> {
    const response = await apiClient.post<ProductIngredient>(
      `/restaurant/my/products/${productId}/ingredients`,
      dto,
    );
    return response.data.data;
  },

  async updateProductIngredient(
    ingredientId: string,
    dto: Partial<{ name: string; displayOrder: number; isActive: boolean }>,
  ): Promise<ProductIngredient> {
    const response = await apiClient.patch<ProductIngredient>(
      `/restaurant/my/ingredients/${ingredientId}`,
      dto,
    );
    return response.data.data;
  },

  async deleteProductIngredient(ingredientId: string): Promise<void> {
    await apiClient.delete(`/restaurant/my/ingredients/${ingredientId}`);
  },

  // ============================================================
  // PRODUCT GROUP ASSIGNMENT
  // ============================================================

  /**
   * Assign groups to product
   */
  async assignGroupsToProduct(
    productId: string,
    dto: {
      modifierGroups?: Array<{
        modifierGroupId: string;
        isRequired?: boolean;
        minSelection?: number;
        maxSelection?: number | null;
        displayOrder?: number;
        optionOverrides?: Array<{
          modifierOptionId: string;
          priceOverride?: number | null;
          isEnabled?: boolean;
        }>;
      }>;
      modifierGroupIds?: string[];
    },
  ): Promise<Product> {
    const response = await apiClient.patch<Product>(
      `/restaurant/my/products/${productId}/assign-groups`,
      dto,
    );
    return response.data.data;
  },
};
